import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, TrendingUp, Target, Database, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

function SaturationBar({ value }) {
  const color = value >= 80 ? 'bg-amber-500' : value >= 50 ? 'bg-emerald-600' : 'bg-sky-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]" data-testid="saturation-bar">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs text-slate-600 font-medium tabular-nums w-9 text-right">{value}%</span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900 bg-slate-50 border-slate-200',
    amber: 'text-amber-900 bg-amber-50 border-amber-200',
    emerald: 'text-emerald-900 bg-emerald-50 border-emerald-200',
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 opacity-70" />
        <span className="text-xs uppercase tracking-wide opacity-70">{label}</span>
      </div>
      <div className="text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

export default function AdminScraperCoverage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (stateFilter) params.append('state', stateFilter.toUpperCase());
        params.append('limit', '500');
        const res = await api.get(`/admin/scraper-coverage?${params.toString()}`);
        setData(res.data);
      } catch {
        toast.error('Erro ao carregar cobertura do scraper');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api, stateFilter]);

  const filtered = useMemo(() => {
    if (!data?.combos) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.combos;
    return data.combos.filter(
      (c) =>
        c.specialty.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.state.toLowerCase().includes(q)
    );
  }, [data, search]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 text-sm"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar ao Admin
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Cobertura do Scraper</h1>
          </div>
          <p className="text-slate-600 max-w-2xl">
            Varredura do Google Maps por especialidade + cidade. Cada combo avança uma janela de 30 itens por busca; quando chega ao fim (skip &gt; 100) o cursor reinicia.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <StatCard icon={TrendingUp} label="Combos mapeados" value={data?.total_combos ?? 0} />
              <StatCard icon={AlertCircle} label="Saturados (≥80%)" value={data?.saturated_combos ?? 0} tone="amber" />
              <StatCard icon={Database} label="CNPJs importados" value={data?.total_imported ?? 0} tone="emerald" />
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por especialidade, cidade ou UF..."
                    className="pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="coverage-search-input"
                  />
                </div>
                <Input
                  placeholder="UF (ex: BA)"
                  className="sm:w-32"
                  maxLength={2}
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
                  data-testid="coverage-state-filter"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="coverage-table">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Especialidade</th>
                      <th className="text-left px-4 py-3 font-semibold">Cidade / UF</th>
                      <th className="text-left px-4 py-3 font-semibold">Saturação</th>
                      <th className="text-right px-4 py-3 font-semibold">Cursor</th>
                      <th className="text-right px-4 py-3 font-semibold">Próx. Skip</th>
                      <th className="text-right px-4 py-3 font-semibold">Última Busca</th>
                      <th className="text-right px-4 py-3 font-semibold">CNPJs</th>
                      <th className="text-right px-4 py-3 font-semibold">Buscas</th>
                      <th className="text-right px-4 py-3 font-semibold">Atualizado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-12 text-slate-400">
                          Nenhum combo encontrado.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((c, idx) => (
                        <tr key={`${c.specialty}-${c.city}-${c.state}-${idx}`} className="hover:bg-slate-50" data-testid={`coverage-row-${idx}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{c.specialty}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {c.city} <span className="text-slate-400">/ {c.state || '—'}</span>
                          </td>
                          <td className="px-4 py-3"><SaturationBar value={c.saturation} /></td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{c.last_skip}</td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            {c.next_skip === 0 && c.last_skip > 0 ? (
                              <span className="text-amber-600 text-xs font-medium">reset</span>
                            ) : (
                              <span className="text-slate-700">{c.next_skip}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700">{c.last_total}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">{c.imported_count}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-500">{c.scrape_count}</td>
                          <td className="px-4 py-3 text-right text-xs text-slate-500">{formatDate(c.last_scraped_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
